import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Script from 'next/script';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { useContextState } from '../StateProvider';
import BaseContext from '../../containers/BasePage/BaseContext';
import { getSearchSuggestions } from '../../utils/api';
import { getRekAITreeFromUrls, getRekAIParams } from '../../utils/rekai';
import trackEvent from '../../utils/trackEvent';
import FadeIn from '../../transitions/FadeIn';
import styles from './SearchInput.module.scss';

// For testing locally
const _rekaiProjectId = process.env.NEXT_PUBLIC_REKAI_PROJECT_ID || undefined;

const useDebounce = (value, delay = 250) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(timeout);
    }, [delay, value]);

    return debouncedValue;
};

/*
 * Suggestions should be shown only when:
 * - SearchInput is focused and there are suggestions
 * - A search suggestion is focused
 */

const SearchInput = ({
    id = 'search',
    name = 'query',
    isVisible = true,
    isInNavigation = false,
    modifier = '',
    setInputChanged = () => {},
    useRekaiAutocomplete = true,
    showRekaiQNA = true,
    submitFormTrigger = () => {},
}) => {
    const { t } = useTranslation('common');
    const [{ searchInput, searchSettings } = {}, dispatch] = useContextState();
    const { isStorybook, rekaiId, rekaiReady, setRekaiReady, rekaiAutocompleteReady, setRekaiAutocompleteReady } =
        useContext(BaseContext);

    const containerRef = useRef();

    const useRekai = !!rekaiId && useRekaiAutocomplete;

    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focused, setFocused] = useState('');
    const [selection, setSelection] = useState(null);
    const [rekaiSearch, setRekaiSearch] = useState(null);
    const [showRekaiAutocomplete, setShowRekaiAutocomplete] = useState(false);

    const defaultDebouncedInput = isInNavigation ? inputValue : searchInput;
    const debouncedInput = useDebounce(defaultDebouncedInput);

    const params = getRekAIParams({
        addcontent: true,
        ...(showRekaiQNA && { entitytype: 'all' }),
        text_read_about_answer: t('search.qnaReadMore'),
        text_read_about_answer_no_title: t('search.qnaReadMoreNoTitle'),
        text_read_about_answer_link: t('search.qnaReadMoreLink'),
        ...(isStorybook && { advanced_mockdata: true }),
    });

    useEffect(() => {
        if (rekaiAutocompleteReady && useRekai) {
            initAutocomplete();
        }
    }, [rekaiAutocompleteReady]);

    useEffect(() => {
        if (isInNavigation && modifier === 'NavigationMobile') {
            if (showRekaiAutocomplete) {
                document.body.classList.add('rekai-autocomplete--open');
            } else {
                document.body.classList.remove('rekai-autocomplete--open');
            }
        }
    }, [showRekaiAutocomplete, isInNavigation, modifier]);

    const initAutocomplete = () => {
        const rekAutocomplete = rekai_autocomplete(
            `#${id}`,
            {
                ...(_rekaiProjectId && { projectid: _rekaiProjectId }),
                params: params,
            },
            [
                {
                    templates: {
                        body: function (rekaiSuggestions) {
                            // Docs: https://docs.rek.ai/search-autocomplete/search-autocomplete-html
                            const qnas = rekaiSuggestions.filter((item) => showRekaiQNA && 'question' in item);
                            const pages = rekaiSuggestions.filter((item) => !(showRekaiQNA && 'question' in item));
                            return [
                                { suggestions: pages },
                                ...(showRekaiQNA && qnas.length > 0
                                    ? [
                                          {
                                              suggestions: qnas,
                                              titleHtml: `<h2>${t('searchForm.qnaTitle')}</h2>`,
                                          },
                                      ]
                                    : []),
                            ];
                        },
                    },
                },
            ]
        );
        setRekaiSearch(rekAutocomplete);

        rekAutocomplete.on('rekai_autocomplete:selected', function (event, suggestion) {
            event.preventDefault();
            rekAutocomplete.autocomplete.close();
            setSelection(suggestion);
        });
        rekAutocomplete.on('rekai_autocomplete:opened', function () {
            setShowRekaiAutocomplete(true);
        });
        rekAutocomplete.on('rekai_autocomplete:closed', function () {
            setShowRekaiAutocomplete(false);
        });
        rekAutocomplete.on('rekai_autocomplete:shown', function (event) {
            if (containerRef && containerRef?.current) {
                const items = containerRef.current.querySelectorAll('.rekai-question');
                if (items.length > 0) {
                    for (let i = 0; i < items.length; i++) {
                        const link = items[i].querySelector('a.rekai-accordion__link');
                        const input = document.getElementById(id);
                        link.addEventListener('click', () => {
                            trackEvent('rekai_auto_search', {
                                rekai_type: 'qna',
                                rekai_search_term: input.value,
                                rekai_search_proposal: link.href,
                            });
                        });
                    }
                }
            }
        });
    };

    useEffect(() => {
        if (selection) {
            if (useRekai) {
                trackEvent('rekai_auto_search', {
                    rekai_type: 'page',
                    rekai_search_term: inputValue,
                    rekai_search_proposal: selection.url,
                });
            }
            if (isStorybook) {
                console.log('Rek AI autocomplete selected', inputValue, selection);
            } else {
                // When autocomplete selection is clicked, navigate to that page
                if (!isInNavigation) {
                    dispatch({ type: 'set_current_page', payload: 1 });
                    dispatch({ type: 'set_searching', payload: true });
                }
                window.location = selection.url;
            }
        }
    }, [selection]);

    useEffect(() => {
        const getItems = async () => {
            const items = await getSearchSuggestions(debouncedInput, searchSettings?.parent || '');
            setSuggestions(items);
        };
        if (debouncedInput && !useRekai) {
            getItems();
        }
    }, [debouncedInput, searchSettings?.parent]);

    /* Key handling */
    useEffect(() => {
        const UP = 38;
        const DOWN = 40;
        const ESC = 27;
        const ENTER = 13;

        const handleBlockScroll = (e) => {
            if (e.keyCode === DOWN || e.keyCode === UP) {
                e.preventDefault();
            }
        };
        const handleKeyPress = (e) => {
            const currentElement = document.activeElement;
            const keyCode = e.keyCode;
            const isLink = currentElement
                ? currentElement.classList.value.indexOf(styles['SearchInput__Link']) > -1
                : '';
            const isSearch = currentElement.id === id;

            if (isSearch && keyCode === DOWN) {
                document.querySelector('#suggestions .SearchInput__Link:first-of-type').focus();
            }

            if (isSearch && keyCode === ENTER) {
                setShowSuggestions(false);

                if (!isInNavigation) {
                    dispatch({ type: 'set_current_page', payload: 1 });
                    dispatch({ type: 'set_searching', payload: true });
                }
            }

            // We are at the first suggestion and press up
            if (!isSearch && isLink && keyCode === UP && !currentElement.parentElement.previousSibling) {
                // Takes the links parent (li) and finds its next sibling (li)
                // and finds its link and focusses
                document.getElementById(id).focus();
                currentElement.parentElement.previousSibling.querySelector('a').focus();
            }

            // We are not at the first suggestion and press up
            if (!isSearch && isLink && keyCode === UP && currentElement.parentElement.previousSibling) {
                // Takes the links parent (li) and finds its previous sibling (li)
                // and finds its link and focusses
                currentElement.parentElement.previousSibling.querySelector('a').focus();
            }

            // We are not at a link and press down
            if (!isSearch && isLink && keyCode === DOWN && currentElement.parentElement.nextSibling) {
                // Takes the links parent (li) and finds its next sibling (li)
                // and finds its link and focusses
                currentElement.parentElement.nextSibling.querySelector('a').focus();
            }

            if (keyCode === ESC) {
                // Remove focus
                document.getElementById(id).focus();
                setShowSuggestions(false);
            }
        };

        const handleSubmit = (e) => {
            const currentElement = document.activeElement;
            const isSearch = currentElement.id === id;
            if (isSearch && e.keyCode === ENTER) {
                submitFormTrigger();
            }
        };

        if (!useRekai) {
            document.addEventListener('keyup', handleKeyPress);
            document.addEventListener('keydown', handleBlockScroll);
        } else {
            document.addEventListener('keyup', handleSubmit);
        }

        return () => {
            if (!useRekai) {
                document.removeEventListener('keyup', handleKeyPress);
                document.removeEventListener('keydown', handleBlockScroll);
            } else {
                document.removeEventListener('keyup', handleSubmit);
            }
        };
    }, [dispatch]);

    useEffect(() => {
        const focusShouldShow =
            (focused && focused.id == id) ||
            focused.localName == 'li' ||
            (focused && focused.classList.contains(styles['SearchInput__Link'])) ||
            (focused && focused.classList.contains(styles['SearchInput__Button'])) ||
            (focused && focused.classList.contains(styles['SearchInput__Reset']));

        if (!useRekai) {
            if (focusShouldShow) {
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
            }
        }
    }, [suggestions, focused]);

    useEffect(() => {
        if (isVisible && isInNavigation) {
            // Delay focus, making sure rekai has loaded first
            if (!useRekai || rekaiReady) {
                setTimeout(() => {
                    const searchField = document.getElementById(id);
                    if (searchField) {
                        searchField.focus();
                    }
                }, 100);
            }
        }
    }, [isVisible, isInNavigation, useRekai, rekaiReady]);

    const changeHandler = (e) => {
        setFocused(e.target);
        setInputValue(e.target.value);
        if (!isInNavigation) {
            dispatch({
                type: 'set_search_input',
                payload: e.target.value,
            });
        } else {
            setInputChanged(e.target.value);
        }
    };

    const resetClickHandler = (e) => {
        if (!isInNavigation) {
            dispatch({
                type: 'reset_search_input',
            });
            dispatch({
                type: 'set_current_page',
                payload: 1,
            });
            dispatch({
                type: 'set_searching',
                payload: true,
            });
        } else {
            document.getElementById(id).value = '';
        }

        if (useRekai && rekaiSearch && rekaiSearch?.autocomplete) {
            rekaiSearch.autocomplete.setVal('');
            // Close dropdown when clearing on mobile
            rekaiSearch.autocomplete.close();
        }
    };

    const suggestionClickHandler = () => {
        if (!isInNavigation) {
            dispatch({
                type: 'set_current_page',
                payload: 1,
            });
            dispatch({
                type: 'set_searching',
                payload: true,
            });
        }
        setShowSuggestions(false);
    };

    const classes = classNames(styles['SearchInput'], {
        [styles[`SearchInput--${modifier}`]]: modifier,
        [styles['SearchInput--RekAI']]: useRekai,
        [styles['SearchInput--InNavigation']]: isInNavigation,
        [styles['SearchInput--SearchPage']]: !isInNavigation,
        [styles['SearchInput--ShowsSuggestions']]: showSuggestions,
    });
    const suggestionsClasses = classNames(styles['SearchInput__Suggestions'], {
        [styles['SearchInput__Suggestions--HasSuggestions']]: true,
    });

    return (
        <>
            {useRekai && (
                <>
                    <Script
                        id="rekai-init"
                        src={`https://static.rekai.se/${rekaiId}.js`}
                        onReady={() => {
                            setRekaiReady(true);
                        }}
                    />
                    <Script
                        id="rekai-autocomplete-init"
                        src="https://static.rekai.se/addon/v3/rekai_autocomplete.min.js"
                        strategy="lazyOnload"
                        onReady={() => {
                            setRekaiAutocompleteReady(true);
                        }}
                    />
                </>
            )}
            <div className={classes} ref={containerRef}>
                <div className={styles['SearchInput__Wrap']}>
                    <div className={styles['SearchInput__Field']}>
                        <input
                            className={styles['SearchInput__Input']}
                            value={searchInput}
                            placeholder={t('search.placeholder')}
                            id={id}
                            name={name}
                            onChange={(e) => changeHandler(e)}
                        />
                        {(isInNavigation ? inputValue : searchInput) !== '' && (
                            <button
                                type="reset"
                                onClick={() => resetClickHandler()}
                                className={styles['SearchInput__Reset']}
                                aria-label={t('search.resetSearchAriaLabel')}
                            />
                        )}
                    </div>
                    <button
                        type="submit"
                        className={styles['SearchInput__Button']}
                        aria-label={t('search.searchAriaLabel')}>
                        <span className={styles['SearchInput__ButtonText']}>{t('search.button')}</span>
                    </button>

                    <FadeIn visible={showSuggestions}>
                        <div role="region" aria-live="polite" id={`${id}-suggestions`} className={suggestionsClasses}>
                            <ol>
                                {suggestions.map((item) => {
                                    return (
                                        <li key={item.href}>
                                            <a className={styles['SearchInput__Link']} href={item.href}>
                                                {item.title}
                                            </a>
                                        </li>
                                    );
                                })}
                                {searchInput && (
                                    <li className={styles['SearchInput__SearchLink']}>
                                        <button
                                            type="button"
                                            className={styles['SearchInput__Link']}
                                            onClick={() => suggestionClickHandler()}>
                                            {`${t('search.searchAriaLabel')}: "${searchInput}"`}
                                        </button>
                                    </li>
                                )}
                            </ol>
                        </div>
                    </FadeIn>
                </div>
            </div>
        </>
    );
};

SearchInput.propTypes = {
    id: PropTypes.string.isRequired,
    isVisible: PropTypes.bool,
    isInNavigation: PropTypes.bool,
    submitFormTrigger: PropTypes.func,
};

export default SearchInput;
